import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const HeroSearchSection = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('rent');
  const [location, setLocation] = useState('');
  const [budget, setBudget] = useState('');
  const [bhkConfig, setBhkConfig] = useState('');
  const [radius, setRadius] = useState('6');

  const propertyTypeOptions = [
    { value: 'rent', label: 'Rent' },
    { value: 'buy', label: 'Buy' }
  ];

  const budgetOptions = [
    { value: '10000-25000', label: '₹10,000 - ₹25,000' },
    { value: '25000-50000', label: '₹25,000 - ₹50,000' },
    { value: '50000-100000', label: '₹50,000 - ₹1,00,000' },
    { value: '100000-200000', label: '₹1,00,000 - ₹2,00,000' },
    { value: '200000+', label: '₹2,00,000+' }
  ];

  const bhkOptions = [
    { value: '1', label: '1 BHK' },
    { value: '2', label: '2 BHK' },
    { value: '3', label: '3 BHK' },
    { value: '4', label: '4+ BHK' }
  ];

  const radiusOptions = [
    { value: '2', label: '2 km' },
    { value: '6', label: '6 km' },
    { value: '10', label: '10 km' },
    { value: '15', label: '15 km' }
  ];

  const locationSuggestions = [
    'Koramangala, Bangalore',
    'Bandra West, Mumbai',
    'Connaught Place, Delhi',
    'Anna Nagar, Chennai',
    'Hitech City, Hyderabad',
    'Salt Lake, Kolkata'
  ];

  const handleSearch = () => {
    navigate('/property-search-discovery', {
      state: {
        searchType,
        location,
        budget,
        bhkConfig,
        radius
      }
    });
  };

  return (
    <section className="relative min-h-[600px] bg-gradient-to-br from-primary/5 via-secondary/5 to-canvas flex items-center">
      {/* Background Map Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%232563EB%22%20fill-opacity%3D%220.4%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] bg-repeat"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-text-primary mb-6">
            Properties
            <span className="text-primary block font-accent">Perfect Space</span>
          </h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Find your ideal home with intelligent search, neighborhood insights, and direct owner connections
          </p>
        </div>

        {/* Search Interface */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-surface rounded-2xl shadow-modal p-6 md:p-8 border border-border">
            {/* Property Type Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-muted rounded-lg p-1 flex">
                {propertyTypeOptions.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSearchType(type.value)}
                    className={`px-6 py-3 rounded-md font-medium transition-smooth ${
                      searchType === type.value
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="lg:col-span-2">
                <Input
                  label="Location"
                  type="text"
                  placeholder="Search by city, area, or landmark"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full"
                />
                {location && (
                  <div className="mt-2 bg-surface border border-border rounded-lg shadow-property-card">
                    {locationSuggestions
                      .filter(suggestion => 
                        suggestion.toLowerCase().includes(location.toLowerCase())
                      )
                      .slice(0, 3)
                      .map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => setLocation(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-muted transition-quick border-b border-border last:border-b-0 flex items-center space-x-3"
                        >
                          <Icon name="MapPin" size={16} className="text-text-secondary" />
                          <span className="text-text-primary">{suggestion}</span>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>

              <Select
                label="Budget"
                placeholder="Select budget range"
                options={budgetOptions}
                value={budget}
                onChange={setBudget}
              />

              <Select
                label="BHK"
                placeholder="Select BHK"
                options={bhkOptions}
                value={bhkConfig}
                onChange={setBhkConfig}
              />
            </div>

            {/* Advanced Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Select
                label="Search Radius"
                options={radiusOptions}
                value={radius}
                onChange={setRadius}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  fullWidth
                  iconName="SlidersHorizontal"
                  iconPosition="left"
                >
                  More Filters
                </Button>
              </div>

              <div className="flex items-end">
                <Button
                  variant="ghost"
                  fullWidth
                  iconName="MapPin"
                  iconPosition="left"
                >
                  Use My Location
                </Button>
              </div>
            </div>

            {/* Search Button */}
            <div className="text-center">
              <Button
                variant="default"
                size="lg"
                onClick={handleSearch}
                iconName="Search"
                iconPosition="left"
                className="px-12"
              >
                Search Properties
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">50,000+</div>
              <div className="text-sm text-text-secondary">Active Properties</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary">25,000+</div>
              <div className="text-sm text-text-secondary">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-trust-builder">100+</div>
              <div className="text-sm text-text-secondary">Cities Covered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-conversion-cta">24/7</div>
              <div className="text-sm text-text-secondary">Support Available</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSearchSection;